import type { PageSession } from '@p2b/contract';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useMe } from '../App.js';
import { api, P2bError, projectRef } from '../api.js';
import { useI18n } from '../i18n.js';
import { Icon, Lockup } from '../ui.js';

/** The sandbox AI-written pages run in (docs/PAGES.md §3). Never add allow-same-origin. */
const SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals';

const encodePath = (p: string) => p.split('/').map(encodeURIComponent).join('/');
const decodePath = (p: string) =>
  p
    .split('/')
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    })
    .join('/');

/** Which page a workspace path belongs to: the page with that entry, else the deepest folder holding it. */
function pageOf(sess: PageSession, path: string) {
  return (
    sess.pages.find((p) => p.entry === path) ??
    sess.pages.filter((p) => path.startsWith(p.root)).sort((a, b) => b.root.length - a.root.length)[0] ??
    null
  );
}

/**
 * /r/<org>/<project>/<publication|draft>/<workspace path>: a page of a publication, in a sandboxed frame on the
 * content origin. The page reports where it is (p2b bridge, `hello`) so the address bar can be linked; a link into
 * the model viewer's folder asks the shell to open the viewer (`open-viewer`).
 */
export function PageShell() {
  const me = useMe();
  const { t } = useI18n();
  const loc = useLocation();
  const navigate = useNavigate();
  const { org = '', project = '', pub = '' } = useParams();
  const ref = projectRef(org, project);
  const prefix = `/r/${org}/${project}/${pub}/`;
  const path = decodePath(loc.pathname.startsWith(prefix) ? loc.pathname.slice(prefix.length) : '');

  const sess = useQuery({
    queryKey: ['page-session', ref, pub],
    queryFn: () => api.pageSession(ref, pub),
    enabled: !!me.data,
    staleTime: Infinity,
    retry: false,
  });
  const token = useMemo(() => /\/s\/([^/]+)\/$/.exec(sess.data?.contentBase ?? '')?.[1] ?? null, [sess.data]);
  const frame = useRef<HTMLIFrameElement>(null);
  // The frame's address is set when the session arrives or a tab is picked, never from the bar it updates.
  const [src, setSrc] = useState<string | null>(null);
  const [current, setCurrent] = useState(path);
  const [pageTitle, setPageTitle] = useState('');

  useEffect(() => {
    if (!sess.data || src) return;
    const target = path || sess.data.pages[0]?.entry || '';
    setSrc(`${sess.data.contentBase}${encodePath(target)}${loc.search}${loc.hash}`);
    setCurrent(target);
    // Only once per session.
  }, [sess.data]);

  useEffect(() => {
    if (!sess.data || !token) return;
    const s = sess.data;
    const onMessage = (e: MessageEvent) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      const d = e.data as { p2b?: number; t?: string; type?: string; [k: string]: unknown };
      if (!d || d.p2b !== 1 || d.t !== token) return;
      if (d.type === 'hello' && typeof d.path === 'string') {
        const search = typeof d.search === 'string' ? d.search : '';
        const hash = typeof d.hash === 'string' ? d.hash : '';
        setCurrent(d.path);
        if (typeof d.title === 'string') setPageTitle(d.title);
        navigate(`${prefix}${encodePath(d.path)}${search}${hash}`, { replace: true });
      } else if (d.type === 'open-viewer' && typeof d.url === 'string') {
        const viewer = s.viewer ? new URL(s.viewer.url) : null;
        let u: URL;
        try {
          u = new URL(d.url);
        } catch {
          return;
        }
        // Only this project's viewers, on the app's own origin.
        if (viewer && u.origin === viewer.origin && u.pathname.startsWith(`/p/${s.org}/${s.project}/`))
          window.location.assign(`${window.location.origin}${u.pathname}${u.search}${u.hash}`);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sess.data, token, navigate, prefix]);

  const page = sess.data ? pageOf(sess.data, current) : null;
  useEffect(() => {
    if (sess.data) document.title = `${pageTitle || page?.title || t('pages')} · ${sess.data.projectName}`;
  }, [sess.data, page, pageTitle, t]);

  if (me.isPending) return <div className="center muted">{t('loading')}</div>;
  if (me.error instanceof P2bError && me.error.status === 401)
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.error || sess.error)
    return (
      <div className="center error">
        {sess.error instanceof P2bError && sess.error.status === 404
          ? t('pageGone')
          : `${t('error')}: ${(me.error ?? sess.error)!.message}`}
      </div>
    );
  if (!sess.data) return <div className="center muted">{t('loading')}</div>;
  const s = sess.data;
  const open = (entry: string) => {
    setSrc(`${s.contentBase}${encodePath(entry)}`);
    setCurrent(entry);
    navigate(`${prefix}${encodePath(entry)}`, { replace: false });
  };

  return (
    <div className="page-shell">
      <header className="page-bar">
        <Link to="/" className="brand page-brand" aria-label="Mynstra BIM">
          <Lockup />
        </Link>
        <Link to={`/projects/${org}/${project}`} className="page-project">
          <span className="page-project-name">{s.projectName}</span>
          <span className="muted">{pub === 'draft' ? t('draftPages') : s.title || s.revision}</span>
        </Link>
        <nav className="page-tabs" aria-label={t('pages')}>
          {s.pages.map((p) => (
            <button
              key={p.id}
              className={`page-tab${page?.id === p.id ? ' active' : ''}`}
              aria-current={page?.id === p.id ? 'page' : undefined}
              onClick={() => open(p.entry)}
            >
              {p.title}
            </button>
          ))}
        </nav>
        {s.viewer && (
          <a className="button open-model page-viewer" href={s.viewer.url}>
            <Icon.Model />
            <span>{t('openViewer')}</span>
          </a>
        )}
      </header>
      {s.pages.length === 0 ? (
        <p className="empty center">{t('noPages')}</p>
      ) : (
        src && (
          <iframe
            ref={frame}
            className="page-frame"
            title={page?.title ?? t('pages')}
            src={src}
            sandbox={SANDBOX}
            allow="fullscreen; clipboard-write"
            referrerPolicy="no-referrer"
          />
        )
      )}
    </div>
  );
}
